import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Search, Filter, X, MapPin } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Badge } from '../components/ui/badge';
import { ProviderCard } from '../components/provider/ProviderCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProviderList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, language } = useLanguage();
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState({});
  const [ranks, setRanks] = useState({});
  const [countries, setCountries] = useState({});
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [rank, setRank] = useState(searchParams.get('rank') || '');
  const [selectedCountry, setSelectedCountry] = useState(searchParams.get('country') || '');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || '');
  const [priceRange, setPriceRange] = useState([0, 150]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [categoriesRes, ranksRes, countriesRes] = await Promise.all([
          fetch(`${API_URL}/api/config/categories`),
          fetch(`${API_URL}/api/config/ranks`),
          fetch(`${API_URL}/api/config/countries`)
        ]);
        
        if (categoriesRes.ok) setCategories((await categoriesRes.json()).categories);
        if (ranksRes.ok) setRanks((await ranksRes.json()).ranks);
        if (countriesRes.ok) setCountries((await countriesRes.json()).countries);
      } catch (error) {
        console.error('Error fetching filters:', error);
      }
    };
    fetchFilters();
  }, []);

  useEffect(() => {
    if (selectedCountry && selectedCountry !== 'all') {
      fetch(`${API_URL}/api/config/cities/${selectedCountry}`).then(r => r.json()).then(d => setCities(d.cities || []));
    } else {
      setCities([]);
      setSelectedCity('');
    }
  }, [selectedCountry]);

  useEffect(() => {
    const fetchProviders = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (category && category !== 'all') params.set('category', category);
        if (rank && rank !== 'all') params.set('rank', rank);
        if (priceRange[0] > 0) params.set('min_rate', priceRange[0]);
        if (priceRange[1] < 150) params.set('max_rate', priceRange[1]);
        if (selectedCountry && selectedCountry !== 'all') params.set('country', selectedCountry);
        if (selectedCity && selectedCity !== 'all') params.set('city', selectedCity);
        
        const response = await fetch(`${API_URL}/api/providers?${params.toString()}`);
        if (response.ok) {
          const data = await response.json();
          setProviders(data);
        }
      } catch (error) {
        console.error('Error fetching providers:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProviders();
  }, [search, category, rank, priceRange, selectedCountry, selectedCity]);

  const clearFilters = () => {
    setSearch('');
    setCategory('');
    setRank('');
    setSelectedCountry('');
    setSelectedCity('');
    setPriceRange([0, 150]);
    setSearchParams({});
  };

  const activeFiltersCount = [
    search,
    category && category !== 'all',
    rank && rank !== 'all',
    selectedCountry && selectedCountry !== 'all',
    selectedCity && selectedCity !== 'all',
    priceRange[0] > 0 || priceRange[1] < 150
  ].filter(Boolean).length;

  const getCategoryName = (key) => {
    return categories[key]?.[language] || categories[key]?.['en'] || key;
  };

  const getRankName = (key) => {
    return ranks[key]?.names?.[language] || ranks[key]?.names?.['en'] || key;
  };

  const getCountryName = (code) => {
    return countries[code]?.name?.[language] || countries[code]?.name?.['en'] || code;
  };

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Destination - hidden for Hamburg-only phase */}
      {/*
      <div className="space-y-3">
        <label className="text-sm font-medium flex items-center gap-1"><MapPin className="w-4 h-4" /> Destination</label>
        <Select value={selectedCountry} onValueChange={setSelectedCountry}>
          <SelectTrigger data-testid="filter-country">
            <SelectValue placeholder="Tous les pays" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les pays</SelectItem>
            {Object.entries(countries).map(([code, info]) => (
              <SelectItem key={code} value={code}>{info.name?.[language] || info.name?.en || code}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {cities.length > 0 && (
          <Select value={selectedCity} onValueChange={setSelectedCity}>
            <SelectTrigger data-testid="filter-city">
              <SelectValue placeholder="Toutes les villes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les villes</SelectItem>
              {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      */}

      <div className="space-y-3">
        <label className="text-sm font-medium">{t('nav.services')}</label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="filter-category">
            <SelectValue placeholder={t('home.allCategories')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('home.allCategories')}</SelectItem>
            {Object.keys(categories).map((key) => (
              <SelectItem key={key} value={key}>{getCategoryName(key)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <label className="text-sm font-medium">{t('ranks.pro')}</label>
        <Select value={rank} onValueChange={setRank}>
          <SelectTrigger data-testid="filter-rank">
            <SelectValue placeholder="All Ranks" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Ranks</SelectItem>
            {Object.keys(ranks).map((key) => (
              <SelectItem key={key} value={key}>{getRankName(key)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <label className="text-sm font-medium">
          {t('common.priceRange')}: {priceRange[0]}€ - {priceRange[1]}€
        </label>
        <Slider
          value={priceRange}
          onValueChange={setPriceRange}
          min={0}
          max={150}
          step={5}
          className="py-4"
          data-testid="filter-price"
        />
      </div>

      <Button
        variant="outline"
        className="w-full"
        onClick={clearFilters}
        data-testid="clear-filters"
      >
        {t('common.clearFilters')}
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="provider-list-page">
      <div className="container-app">
        {/* Header */}
        <div className="mb-10">
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold mb-3">
            {t('nav.providers')}
          </h1>
          <p className="text-muted-foreground">
            {t('home.featuredSubtitle')}
          </p>
        </div>

        {/* Search & Filters Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('home.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-12"
              data-testid="search-providers"
            />
          </div>

          {/* Desktop Filters - Destination hidden for Hamburg-only phase */}
          <div className="hidden lg:flex items-center gap-3">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-48 h-12" data-testid="category-filter-desktop">
                <SelectValue placeholder={t('nav.services')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('home.allCategories')}</SelectItem>
                {Object.keys(categories).map((key) => (
                  <SelectItem key={key} value={key}>{getCategoryName(key)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={rank} onValueChange={setRank}>
              <SelectTrigger className="w-40 h-12" data-testid="rank-filter-desktop">
                <SelectValue placeholder="Rank" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ranks</SelectItem>
                {Object.keys(ranks).map((key) => (
                  <SelectItem key={key} value={key}>{getRankName(key)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {activeFiltersCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-all-filters">
                <X className="w-4 h-4 mr-1" />
                {t('common.clearFilters')}
              </Button>
            )}
          </div>

          {/* Mobile Filter Button */}
          <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="lg:hidden h-12" data-testid="mobile-filter-btn">
                <Filter className="w-4 h-4 mr-2" />
                {t('common.filters')}
                {activeFiltersCount > 0 && (
                  <Badge className="ml-2 bg-accent text-accent-foreground">
                    {activeFiltersCount}
                  </Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>{t('common.filters')}</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                <FilterContent />
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Active Filters */}
        {(category && category !== 'all') || (rank && rank !== 'all') || (selectedCountry && selectedCountry !== 'all') || (selectedCity && selectedCity !== 'all') ? (
          <div className="flex flex-wrap gap-2 mb-6">
            {selectedCountry && selectedCountry !== 'all' && (
              <Badge variant="secondary" className="px-3 py-1">
                <MapPin className="w-3 h-3 mr-1" />
                {getCountryName(selectedCountry)}
                <button onClick={() => { setSelectedCountry(''); setSelectedCity(''); }} className="ml-2"><X className="w-3 h-3" /></button>
              </Badge>
            )}
            {selectedCity && selectedCity !== 'all' && (
              <Badge variant="secondary" className="px-3 py-1">
                {selectedCity}
                <button onClick={() => setSelectedCity('')} className="ml-2"><X className="w-3 h-3" /></button>
              </Badge>
            )}
            {category && category !== 'all' && (
              <Badge variant="secondary" className="px-3 py-1">
                {getCategoryName(category)}
                <button onClick={() => setCategory('')} className="ml-2">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {rank && rank !== 'all' && (
              <Badge variant="secondary" className="px-3 py-1">
                {getRankName(rank)}
                <button onClick={() => setRank('')} className="ml-2">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
          </div>
        ) : null}

        {/* Results Count */}
        <p className="text-muted-foreground mb-6">
          {loading ? t('common.loading') : `${providers.length} ${t('nav.providers').toLowerCase()}`}
        </p>

        {/* Provider Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="aspect-[4/5] rounded-2xl skeleton" />
            ))}
          </div>
        ) : providers.length === 0 ? (
          <div className="empty-state">
            <Search className="empty-state-icon" />
            <h3 className="empty-state-title">{t('common.noResults')}</h3>
            <p className="empty-state-text">
              {t('common.clearFilters')}
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={clearFilters}
              data-testid="no-results-clear"
            >
              {t('common.clearFilters')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {providers.map((provider) => (
              <ProviderCard key={provider.provider_id} provider={provider} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
